<script lang="ts">
	import { DetailedSelect, Icon, Label } from '@repo/commons/components'
	import type { DetailedSelectItem } from '@repo/commons/models'
	import { hasUniformElementTypeLabels } from '@repo/commons/utils'
	import { dndzone } from 'svelte-dnd-action'
	import { flip } from 'svelte/animate'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	interface Props {
		items?: DetailedSelectItem[]
		selectedItems?: DetailedSelectItem[]
		label?: string
		error?: string
		tooltip?: TooltipProps
		isRequired?: boolean
		searchable?: boolean
		placeholder?: string
		bottomButtonText?: string
		showBottomButton?: boolean
		showNoResultsButton?: boolean
		noResultsButtonText?: string
		maxDisplayItems?: number
		displayDragableTags?: boolean
		onMultiSelect?:
			| ((items: DetailedSelectItem[], lastSelectedItem: DetailedSelectItem | null) => void)
			| null
		onBlur?: (() => void) | null
		onSearch?: (query: string) => void
		onShowAllClicked?: (() => void) | null
		onNoResultsButtonClick?: (searchQuery: string) => void
		id?: string
		enableDragAndDrop?: boolean
		disabled?: boolean
		clearable?: boolean
		remoteItems?: boolean
		isLoading?: boolean
		hasMore?: boolean
		loadingMore?: boolean
		loadMoreThresholdPx?: number
		onLoadMore?: (() => void | Promise<void>) | null
		onTagsReorder?: ((items: DetailedSelectItem[]) => void) | null
		clearSearchOnSelect?: boolean
	}

	let {
		items = [],
		selectedItems = $bindable([]),
		label = '',
		error = '',
		tooltip,
		searchable = true,
		isRequired = false,
		placeholder = 'Select items',
		bottomButtonText = 'See all',
		showBottomButton = false,
		showNoResultsButton = false,
		noResultsButtonText = 'Add',
		maxDisplayItems = 10,
		displayDragableTags = true,
		clearable = false,
		onMultiSelect = null,
		onBlur = null,
		onSearch = () => {},
		onShowAllClicked = null,
		onNoResultsButtonClick = () => {},
		id = `select-with-tags-${Math.random().toString(36).substring(2, 9)}`,
		enableDragAndDrop = true,
		disabled = false,
		remoteItems = false,
		isLoading = false,
		hasMore = false,
		loadingMore = false,
		loadMoreThresholdPx = 80,
		onLoadMore = null,
		onTagsReorder = null,
		clearSearchOnSelect = true
	}: Props = $props()
	const shouldHideElementTypeLabels = $derived(hasUniformElementTypeLabels(items))

	const draggableItems: Array<DetailedSelectItem & { dragId: string }> = $derived.by(() => {
		return selectedItems.map((item: DetailedSelectItem) => ({
			...item,
			dragId: item.id as string
		}))
	})

	function handleMultiSelect(
		items: DetailedSelectItem[],
		lastSelectedItem: DetailedSelectItem | null
	) {
		selectedItems = items
		onMultiSelect?.(items, lastSelectedItem)
	}

	function handleRemoveItem(itemToRemove: DetailedSelectItem, event: MouseEvent) {
		event.stopPropagation()
		event.preventDefault()

		const updatedItems: DetailedSelectItem[] = selectedItems.filter(
			(item: DetailedSelectItem) => item.id !== itemToRemove.id
		)
		selectedItems = updatedItems

		onMultiSelect?.(updatedItems, null)
	}

	function handleDndConsider(
		e: CustomEvent<{ items: Array<DetailedSelectItem & { dragId: string }> }>
	) {
		if (!enableDragAndDrop) return

		const { items: newItems } = e.detail
		const reorderedItems: DetailedSelectItem[] = newItems.map(({ dragId, ...item }) => item)
		selectedItems = reorderedItems
	}

	function handleDndFinalize(
		e: CustomEvent<{ items: Array<DetailedSelectItem & { dragId: string }> }>
	) {
		if (!enableDragAndDrop) return

		const { items: newItems } = e.detail
		const reorderedItems: DetailedSelectItem[] = newItems.map(({ dragId, ...item }) => item)
		selectedItems = reorderedItems

		onTagsReorder?.(reorderedItems)

		onMultiSelect?.(reorderedItems, null)
	}

	function getTagClasses(isDragging: boolean = false): string {
		const dragClasses: string = isDragging ? 'cursor-grabbing shadow-lg scale-105' : 'cursor-grab'
		return enableDragAndDrop ? dragClasses : ''
	}

	const dndOptions: {
		flipDurationMs: number
		dropTargetStyle: { outline: string; outlineOffset: string }
		dragDisabled: boolean
	} = {
		flipDurationMs: 200,
		dropTargetStyle: {
			outline: 'none',
			outlineOffset: '2px'
		},
		dragDisabled: !enableDragAndDrop
	}

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })
</script>

<div class="select-with-tags">
	<DetailedSelect
		{items}
		{selectedItems}
		{label}
		{error}
		{isRequired}
		{searchable}
		{placeholder}
		{bottomButtonText}
		{showBottomButton}
		{showNoResultsButton}
		{noResultsButtonText}
		{maxDisplayItems}
		tooltip={tooltipProps}
		multiSelect={true}
		onMultiSelect={handleMultiSelect}
		{onBlur}
		{onSearch}
		{onShowAllClicked}
		{onNoResultsButtonClick}
		{id}
		{disabled}
		{clearable}
		{remoteItems}
		{isLoading}
		{hasMore}
		{loadingMore}
		{loadMoreThresholdPx}
		{onLoadMore}
		{clearSearchOnSelect}
	/>

	{#if selectedItems.length > 0 && displayDragableTags}
		<div class="tags-container">
			<div
				class="tags-wrapper"
				use:dndzone={{
					items: draggableItems,
					...dndOptions
				}}
				onconsider={handleDndConsider}
				onfinalize={handleDndFinalize}
			>
				{#each draggableItems as item (item.dragId)}
					<div
						class="tag {getTagClasses()}"
						animate:flip={{ duration: 300 }}
						title={enableDragAndDrop ? 'Drag to reorder' : item.title}
					>
						{#if enableDragAndDrop}
							<Icon name="draggable" size="sm" class="text-[#002C4D80]" />
						{/if}
						<span class="tag-text">{item.title}</span>
						{#if item.label && !(shouldHideElementTypeLabels && item.label.isElementType)}
							<Label
								label={item.label.text}
								variant={item.label.variant ?? 'default'}
								color={item.label.color}
								icon={item.label.icon}
								size="sm"
								iconSize="sm"
							/>
						{/if}
						<button
							class="tag-remove-btn"
							onclick={(e) => handleRemoveItem(item, e)}
							title="Remove {item.title}"
							type="button"
						>
							<Icon name="close" size="sm" class="text-inherit" />
						</button>
					</div>
				{/each}
			</div>
		</div>
	{/if}
</div>

<style>
	.select-with-tags {
		@apply w-full;
	}

	.tags-container {
		@apply mt-3 flex flex-col gap-2;
	}

	.tags-wrapper {
		@apply flex flex-wrap gap-2;
		min-height: 2.5rem;
	}

	.tag {
		@apply flex items-center justify-between gap-1 min-h-6 border rounded-xl border-[#003A661A] bg-[#00498011] max-w-full px-2;
		transition: all 0.2s ease;
	}

	.tag-avatar {
		@apply w-5 h-5 rounded-full object-cover flex-shrink-0;
	}

	.tag-text {
		@apply text-sm px-1 truncate max-w-[200px];
	}

	.tag-remove-btn {
		@apply flex items-center justify-center shrink-0 w-5 h-5 rounded-full cursor-pointer text-[#002C4D80] transition-colors;
	}

	.tag-remove-btn:hover,
	.tag-remove-btn:focus-visible {
		@apply bg-[#E4485B1A] text-[#E4485B];
	}

	.tag-remove-btn:focus-visible {
		@apply outline outline-2 outline-offset-1 outline-[#E4485B];
	}

	@media (max-width: 640px) {
		.tag-text {
			@apply max-w-[120px];
		}
	}

	:global(.tags-wrapper [data-dnd-type='zone']) {
		@apply min-h-10;
	}

	:global(.tag[data-dnd-type='item']) {
		@apply relative z-10;
	}

	:global(.tag[data-dnd-type='item']:hover) {
		@apply z-20;
	}
</style>
